/**
 * PaginationControls - Pagination wrapper with page size selector
 *
 * Atomic level: Molecule
 * Composes: Pagination (shadcn), Select
 *
 * Provides pagination controls with:
 * - Records count display ("Showing X-Y of Z")
 * - Page size selector (10, 25, 50, 100, 200)
 * - Previous/Next navigation with page numbers
 *
 * Following GO UI Design System patterns
 */

import React, { useMemo } from 'react';
import { cn } from '../../../../lib/utils';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200] as const;

interface PaginationControlsProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items across all pages */
  totalItems: number;
  /** Current page size */
  pageSize: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;
  /** Whether pagination is in loading state */
  isLoading?: boolean;
  /** Label for items (defaults to "records") */
  itemLabel?: string;
  /** Additional className for container */
  className?: string;
}

/**
 * Calculate visible page numbers with ellipsis
 */
const getVisiblePages = (
  currentPage: number,
  totalPages: number
): (number | 'ellipsis')[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];

  // Always show first page
  pages.push(1);

  if (currentPage > 3) {
    pages.push('ellipsis');
  }

  // Show pages around current
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 2) {
    pages.push('ellipsis');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
};

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  itemLabel = 'records',
  className,
}) => {
  // Calculate showing range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Get visible page numbers
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages || totalPages === 0;

  return (
    <div
      className={cn(
        'flex flex-col gap-4 border-t border-border/70 px-4 py-4 lg:flex-row lg:items-center lg:justify-between',
        className
      )}
    >
      {/* Left: Records count */}
      <div className="text-sm text-muted-foreground">
        Showing{' '}
        <span className="font-medium text-foreground">
          {startItem.toLocaleString()}-{endItem.toLocaleString()}
        </span>{' '}
        of{' '}
        <span className="font-medium text-foreground">
          {totalItems.toLocaleString()}
        </span>{' '}
        {itemLabel}
      </div>

      {/* Center: Page size selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Show</span>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
          disabled={isLoading}
        >
          <SelectTrigger className="w-[70px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">per page</span>
      </div>

      {/* Right: Pagination navigation */}
      <Pagination className="mx-0 w-auto">
        <PaginationContent>
          {/* Previous button */}
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (!isFirstPage && !isLoading) {
                  onPageChange(currentPage - 1);
                }
              }}
              className={cn(
                isFirstPage || isLoading
                  ? 'pointer-events-none opacity-50'
                  : 'cursor-pointer'
              )}
              aria-disabled={isFirstPage || isLoading}
            />
          </PaginationItem>

          {/* Page numbers */}
          {visiblePages.map((page, index) =>
            page === 'ellipsis' ? (
              <PaginationItem key={`ellipsis-${index}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={page}>
                <PaginationLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isLoading && page !== currentPage) {
                      onPageChange(page);
                    }
                  }}
                  isActive={page === currentPage}
                  className={cn(
                    isLoading ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                  )}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            )
          )}

          {/* Next button */}
          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (!isLastPage && !isLoading) {
                  onPageChange(currentPage + 1);
                }
              }}
              className={cn(
                isLastPage || isLoading
                  ? 'pointer-events-none opacity-50'
                  : 'cursor-pointer'
              )}
              aria-disabled={isLastPage || isLoading}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export { PaginationControls };
export type { PaginationControlsProps };
